import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Checkbox, FormControlLabel, Grid, IconButton, TextField, Typography } from '@mui/material';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { addDays, addHours, addYears, isValid, isWithinInterval, parse, parseISO } from 'date-fns';
import { GenericDialog } from 'app/components/Alerts/GenericDialog';
import { taskDetailsActions } from 'app/components/Tasks/store/Tasks.redux';
import { globalActions } from 'app/ApplicationRoot/Global.redux';
import { DateRangePicker } from 'app/ComponentLibrary/DateRangeNew';
import { FormatDate } from 'app/components/Notification/Tasks/Common/FormatData';
import { getAttachmentIcon } from 'app/components/Notification/Common/AttachmentFileTypes';
import { CreateTaskNewStyles } from './CreateTaskModalNew.styles';
import { FromField, IFromOption } from './FromField';
import { IToRecipient, ToField } from './ToField';
import {
  IInternalTaskModalDetails,
  ITaskComments,
  IUser,
  TaskEntity,
} from 'app/models/Tasks/Tasks';
import { ITaskChainItem } from 'app/models/Tasks/TaskChain';
import { RootTaskCategory } from 'app/models/Tasks/CreateTaskV1';
import { getEmployeeId } from '@AMIEWEB/Notification/Tasks/Common_v2/utils';
import { selectUser } from 'oidc/user.selectors';
import type { IUserInfo } from 'oidc/user.redux';
import { formatCoveredByUsers, generateReplyRecipients } from '../TaskDetails/helper';
import { selectNewTaskCreatedId, selectReplyTaskPlacementEmployees } from 'app/components/Tasks/store/Tasks.selectors';
import { GeneratePlacementEmpObject } from 'app/components/Tasks/CreateTask/FormatData';
import { buildCreateTaskRequestFromNewModal } from './buildCreateTaskRequest';
import { getValidatedAttachments } from './attachmentValidation';

type ReplySourceTask = ITaskComments | IInternalTaskModalDetails | ITaskChainItem | undefined;

interface ReplyToTaskModalNewProps {
  open: boolean;
  parentTaskDetails: IInternalTaskModalDetails;
  container?: TaskEntity;
  sourceTask?: ReplySourceTask;
  onCreated?: (newTaskId: number) => void;
  onClose: () => void;
}

const getDisplayName = (firstName?: string, lastName?: string) =>
  `${firstName || ''} ${lastName || ''}`.trim();

const getRecipient = (id?: number, firstName?: string, lastName?: string, isInactive?: boolean): IToRecipient | null => {
  if (!id) return null;
  const label = getDisplayName(firstName, lastName);
  return {
    object: {
      empId: id,
      label,
      role: '',
      isInactive: !!isInactive,
    },
    label,
  };
};

type TaskUsers = {
  isActive?: boolean;
  active?: boolean;
  status?: string;
  employmentStatus?: string;
  statusId?: number;
  roleDescription?: string | null;
} | null | undefined;

const getIsInactive = (user: TaskUsers) => !!user && user.isActive === false;

const dedupeRecipients = (recipients: Array<IToRecipient | null | undefined>) => {
  const map = new Map<number, IToRecipient>();
  recipients.forEach(recipient => {
    const id = recipient?.object?.empId;
    if (!id) return;
    if (!map.has(id)) map.set(id, recipient as IToRecipient);
  });
  return Array.from(map.values());
};

const getInitialSubject = (subject: string) => {
  const trimmed = (subject || '').trim();
  if (!trimmed) return 'RE: ';
  return /^RE:\s/i.test(trimmed) ? trimmed : `RE: ${trimmed}`;
};

const getCategoryValue = (parentTaskDetails: IInternalTaskModalDetails) => {
  if (parentTaskDetails?.taskCategoryId != null) {
    return String(parentTaskDetails.taskCategoryId);
  }
  const categoryValue = parentTaskDetails?.taskCategory;
  if (typeof categoryValue === 'string') {
    const trimmed = categoryValue.trim();
    if (!trimmed) return 'General';
    const parsedCategory = Number(trimmed);
    if (Number.isInteger(parsedCategory) && parsedCategory > 0) return String(parsedCategory);
    return trimmed;
  }
  if (typeof categoryValue === 'number' && Number.isInteger(categoryValue) && categoryValue > 0) {
    return String(categoryValue);
  }
  return 'General';
};

const toCategoryLabel = (raw: string) => raw.replace(/([a-z0-9])([A-Z])/g, '$1 $2').trim();

const getCategoryDisplayValue = (parentTaskDetails: IInternalTaskModalDetails, categoryValue: string) => {
  const labelFromTask = typeof parentTaskDetails?.taskCategory === 'string' ? parentTaskDetails.taskCategory.trim() : '';
  if (labelFromTask && !Number.isInteger(Number(labelFromTask))) {
    return labelFromTask;
  }

  const categoryId = Number(categoryValue);
  if (Number.isInteger(categoryId)) {
    const enumLabel = RootTaskCategory[categoryId as RootTaskCategory];
    if (typeof enumLabel === 'string' && enumLabel.trim()) {
      return toCategoryLabel(enumLabel);
    }
  }

  return 'General';
};

export const ReplyToTaskModalNew = ({
  open,
  parentTaskDetails,
  container,
  sourceTask,
  onCreated,
  onClose,
}: ReplyToTaskModalNewProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { classes } = CreateTaskNewStyles();
  const userInfo = useSelector(selectUser);
  const newTaskCreatedId = useSelector(selectNewTaskCreatedId);

  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [hasEdits, setHasEdits] = useState(false);

  const [selectedFromOption, setSelectedFromOption] = useState<IFromOption | null>(null);
  const [toRecipients, setToRecipients] = useState<IToRecipient[]>([]);
  const [ccRecipients, setCcRecipients] = useState<IToRecipient[]>([]);
  const replyTaskPlacementEmployees = useSelector(selectReplyTaskPlacementEmployees);

  const [category, setCategory] = useState('');
  const [categoryDisplay, setCategoryDisplay] = useState('');
  const [subject, setSubject] = useState('');
  const [followUpChecked, setFollowUpChecked] = useState(false);
  const [followUpDate, setFollowUpDate] = useState('');
  const [followUpDateError, setFollowUpDateError] = useState<string | null>(null);
  const [priority, setPriority] = useState('Normal');
  const [flagChecked, setFlagChecked] = useState(false);
  const [optionApprovalChecked, setOptionApprovalChecked] = useState(false);
  const [message, setMessage] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [isReplyCreateRequested, setIsReplyCreateRequested] = useState(false);
  const onCreatedRef = useRef(onCreated);
  const onCloseRef = useRef(onClose);
  const initializedForOpenRef = useRef(false);

  const currentUserId = parseInt(getEmployeeId(userInfo?.userInfo)) || 0;

  const isTaskChainSource = !!(sourceTask && 'recipients' in sourceTask && Array.isArray((sourceTask as ITaskChainItem).recipients));

  const sourceFrom = useMemo(() => {
    if (sourceTask && 'createdBy' in sourceTask && sourceTask.createdBy) return sourceTask.createdBy;
    if (isTaskChainSource) {
      const sender = (sourceTask as ITaskChainItem).sender;
      if (sender) return { id: sender.employeeId, firstName: sender.firstName, lastName: sender.lastName };
    }
    return parentTaskDetails?.createdBy;
  }, [sourceTask, isTaskChainSource, parentTaskDetails?.createdBy]);

  const sourceToList = useMemo(() => {
    if (sourceTask && 'tos' in sourceTask) return sourceTask?.tos || [];
    if (isTaskChainSource) {
      return ((sourceTask as ITaskChainItem).recipients || [])
        .filter(r => !r.isCC)
        .map(r => ({ id: r.recipientId, firstName: r.firstName, lastName: r.lastName, statusId: r.statusId, isActive: r.isActive }));
    }
    return parentTaskDetails?.assignedTo || [];
  }, [sourceTask, isTaskChainSource, parentTaskDetails?.assignedTo]);

  const sourceCcList = useMemo(() => {
    if (sourceTask && 'ccs' in sourceTask) return sourceTask?.ccs || [];
    if (isTaskChainSource) {
      return ((sourceTask as ITaskChainItem).recipients || [])
        .filter(r => !!r.isCC)
        .map(r => ({ id: r.recipientId, firstName: r.firstName, lastName: r.lastName, statusId: r.statusId, isActive: r.isActive }));
    }
    return parentTaskDetails?.cc || [];
  }, [sourceTask, isTaskChainSource, parentTaskDetails?.cc]);

  const sourceSubject = useMemo(() => {
    if (sourceTask && 'subject' in sourceTask && sourceTask?.subject) return sourceTask.subject;
    return parentTaskDetails?.task || '';
  }, [sourceTask, parentTaskDetails?.task]);

  const placementEmployees = useMemo(
    () => ((GeneratePlacementEmpObject(replyTaskPlacementEmployees || []) || []) as IToRecipient[]),
    [replyTaskPlacementEmployees],
  );

  useEffect(() => {
    if (!open) {
      initializedForOpenRef.current = false;
      return;
    }
    if (initializedForOpenRef.current) return;
    initializedForOpenRef.current = true;

    const date = new Date();
    date.setHours(23, 59, 59, 999);
    setFollowUpDate(FormatDate(date));
    setFollowUpDateError(null);
    setPriority('Normal');
    setFlagChecked(false);
    setOptionApprovalChecked(false);
    setMessage('');
    setAttachments([]);
    setHasEdits(false);
    setIsReplyCreateRequested(false);
    const initialCategory = getCategoryValue(parentTaskDetails);
    setCategory(initialCategory);
    setCategoryDisplay(getCategoryDisplayValue(parentTaskDetails, initialCategory));
    setFollowUpChecked(!!(parentTaskDetails as any)?.isFollowup);
    setSubject(getInitialSubject(sourceSubject));

    setSelectedFromOption({
      id: currentUserId,
      coveredEmployeeId: currentUserId,
      coveredEmployeeFirstName: userInfo?.userInfo?.firstName || '',
      coveredEmployeeLastName: userInfo?.userInfo?.lastName || '',
      title: '-',
    });

    const coverageActionsForReply =
      sourceTask &&
      'taskCoverageAction' in sourceTask &&
      Array.isArray((sourceTask as ITaskComments).taskCoverageAction)
        ? (sourceTask as ITaskComments).taskCoverageAction
        : parentTaskDetails?.taskCoverageActions;
    const coverageProviders =
      coverageActionsForReply && coverageActionsForReply.length > 0
        ? formatCoveredByUsers(coverageActionsForReply)
        : undefined;

    const createdByForReply = (sourceFrom ?? parentTaskDetails?.createdBy) as IUser | undefined;

    const replyRecipients =
      createdByForReply && userInfo?.userInfo
        ? generateReplyRecipients(
            createdByForReply,
            sourceToList || [],
            sourceCcList || [],
            userInfo.userInfo as IUserInfo,
            coverageProviders,
          )
        : undefined;

    let toPrefill: IToRecipient[];

    if (replyRecipients) {
      toPrefill = dedupeRecipients(
        (replyRecipients.assignedTo || []).map(user =>
          getRecipient(user?.id, user?.firstName, user?.lastName, getIsInactive(user)),
        ),
      );
    } else {
      const sourceFromRecipient = getRecipient(
        sourceFrom?.id,
        sourceFrom?.firstName,
        sourceFrom?.lastName,
        getIsInactive(sourceFrom),
      );
      toPrefill = dedupeRecipients([
        sourceFromRecipient,
        ...(sourceToList || []).map(user =>
          getRecipient(user?.id, user?.firstName, user?.lastName, getIsInactive(user)),
        ),
      ]).filter(
        r =>
          r &&
          r.object.empId != null &&
          Number(r.object.empId) !== Number(currentUserId),
      ) as IToRecipient[];
    }
    setToRecipients(toPrefill);

    let ccPrefill: IToRecipient[];
    if (replyRecipients) {
      ccPrefill = dedupeRecipients(
        (replyRecipients.cc || []).map(user =>
          getRecipient(user?.id, user?.firstName, user?.lastName, getIsInactive(user)),
        ),
      ).filter(recipient => !toPrefill.find(to => to.object.empId === recipient.object.empId));
    } else {
      ccPrefill = dedupeRecipients(
        (sourceCcList || []).map(user =>
          getRecipient(user?.id, user?.firstName, user?.lastName, getIsInactive(user)),
        ),
      ).filter(recipient => !toPrefill.find(to => to.object.empId === recipient.object.empId));
    }
    setCcRecipients(ccPrefill);
  }, [
    open,
    parentTaskDetails?.taskCategory,
    (parentTaskDetails as any)?.isFollowup,
    sourceFrom,
    sourceToList,
    sourceCcList,
    sourceSubject,
    currentUserId,
    userInfo?.userInfo?.firstName,
    userInfo?.userInfo?.lastName,
  ]);

  useEffect(() => {
    if (!open) {
      dispatch(taskDetailsActions.setReplyTaskPlacementEmployees([]));
      return;
    }
    const placementId = parentTaskDetails?.placementId;
    if (!placementId) {
      dispatch(taskDetailsActions.setReplyTaskPlacementEmployees([]));
      return;
    }
    dispatch(taskDetailsActions.getReplyTaskPlacementEmployeesAction(String(placementId)));
  }, [open, parentTaskDetails?.placementId, dispatch]);

  useEffect(() => {
    onCreatedRef.current = onCreated;
    onCloseRef.current = onClose;
  }, [onCreated, onClose]);

  useEffect(() => {
    if (!open) return;
    if (isReplyCreateRequested && newTaskCreatedId && Number(newTaskCreatedId) > 0) {
      onCreatedRef.current?.(Number(newTaskCreatedId));
      dispatch(taskDetailsActions.setNewTaskCreatedId(undefined));
      setIsReplyCreateRequested(false);
      onCloseRef.current?.();
    }
  }, [newTaskCreatedId, open, isReplyCreateRequested, dispatch]);

  const markAsEdited = () => setHasEdits(true);

  const validateFollowUpDate = (value: string): string | null => {
    const trimmed = (value || '').trim();
    if (!trimmed) return t('required');
    if (trimmed.includes('_') || trimmed.includes(' ')) return t('invalidFormat');
    const now = new Date();
    const isoDate = parseISO(trimmed);
    if (isValid(isoDate)) {
      if (isWithinInterval(isoDate, { start: addDays(now, -1), end: addYears(now, 10) })) return null;
      return t('invalidDate');
    }
    const customDate = parse(trimmed, 'MM/dd/yyyy', new Date());
    if (isValid(customDate) && isWithinInterval(customDate, { start: addDays(now, -1), end: addYears(now, 10) }))
      return null;
    return t('invalidDate');
  };

  const hasInactiveRecipients = [...toRecipients, ...ccRecipients].some(recipient => recipient?.object?.isInactive);
  const toIdsSet = new Set(toRecipients.map(r => r.object.empId));
  const hasCrossDuplicates = ccRecipients.some(r => toIdsSet.has(r.object.empId));
  const hasValidTo = toRecipients.length > 0;
  const hasValidSubject = !!subject.trim();
  const hasValidCategory = true;
  const hasValidPriority = !!priority;
  const hasValidFollowUpDate = !!followUpDate && !followUpDateError;
  const isSaveEnabled =
    hasEdits &&
    hasValidTo &&
    hasValidSubject &&
    hasValidCategory &&
    hasValidPriority &&
    hasValidFollowUpDate &&
    !hasInactiveRecipients &&
    !hasCrossDuplicates;
  const optionContainer = container ?? (parentTaskDetails?.placementId ? TaskEntity.PLACEMENTS : TaskEntity.ALL);

  const showAttachmentError = (message: string) => {
    dispatch(globalActions.setSnackBar({ message, severity: 'error' }));
  };

  const handleAddAttachments = (newFiles: File[]) => {
    const nextAttachments = getValidatedAttachments({
      existingAttachments: attachments,
      newFiles,
      t,
      onError: showAttachmentError,
    });
    if (nextAttachments.length !== attachments.length) {
      setAttachments(nextAttachments);
      markAsEdited();
    }
  };

  const handleClose = (_e?: React.MouseEvent, reason?: string) => {
    if (reason === 'backdropClick') return;
    if (hasEdits) {
      setShowDiscardModal(true);
      return;
    }
    onClose();
  };

  const handleSave = () => {
    if (!isSaveEnabled) return;
    const toIds = Array.from(new Set(toRecipients.map(r => r.object.empId)));
    const ccIds = ccRecipients
      .map(recipient => recipient.object.empId)
      .filter(id => !toIds.includes(id));

    const sourceOriginalTaskId =
      sourceTask && 'originalTaskId' in sourceTask ? sourceTask.originalTaskId : undefined;
    const originalTaskId = sourceOriginalTaskId ?? parentTaskDetails?.taskId ?? parentTaskDetails?.rootTaskId;

    const request = buildCreateTaskRequestFromNewModal({
      subject: subject.trim(),
      description: message,
      category,
      followUpDate,
      priority,
      flagChecked,
      optionApprovalChecked,
      followUpChecked,
      toRecipients: toRecipients.filter(r => toIds.includes(r.object.empId)),
      ccRecipients: ccRecipients.filter(r => ccIds.includes(r.object.empId)),
      selectedFromOption,
      getAllIds: {
        placementId: parentTaskDetails?.placementId,
        candidateId: parentTaskDetails?.candidateId,
        orderId: parentTaskDetails?.orderId,
        facilityId: parentTaskDetails?.facilityId,
        unitId: parentTaskDetails?.unitId,
        companyId: parentTaskDetails?.companyId,
      },
      container: optionContainer,
      originalTaskId,
    });

    dispatch(
      taskDetailsActions.saveNewTaskRequest({
        request,
        attachments,
        isReplyTaskNew: true,
      }),
    );
    setIsReplyCreateRequested(true);
  };

  return (
    <>
      <GenericDialog
        open={open}
        fullWidth
        maxWidth="xl"
        variant="blue"
        draggable
        className={classes.modalContainer}
        onClose={handleClose}
        dialogTitleProps={{
          text: t('notification.createTask.newDesign.replyToTaskTitle'),
          closeButton: true,
          expandable: true,
        }}
        dialogContentProps={{ classes: { root: classes.dialogContent } }}
        dialogActions={[
          {
            text: t('notification.createTask.cancelBtn').toUpperCase(),
            variant: 'contained',
            color: 'tertiary',
            onClick: e => handleClose(e as unknown as React.MouseEvent),
          },
          {
            text: t('notification.createTask.saveBtn').toUpperCase(),
            variant: 'contained',
            color: 'primary',
            disabled: !isSaveEnabled,
            onClick: handleSave,
          },
        ]}
      >
        <Grid container spacing={2} className={classes.contentGrid}>
          <Grid item xs={12} className={classes.section}>
            <Box className={classes.fromToCcRow}>
              <Typography variant="body2" component="span" className={classes.fromToCcLabel}>
                {t('notification.createTask.newDesign.fromLabel')}
              </Typography>
              <Box className={classes.fromToCcValue}>
                <FromField
                  value={selectedFromOption}
                  onChange={option => {
                    setSelectedFromOption(option);
                    markAsEdited();
                  }}
                  displayClassName={classes.fromToRow}
                />
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} className={classes.section}>
            <Box className={classes.fromToCcRow}>
              <Typography variant="body2" component="span" className={classes.fromToCcLabel}>
                {t('notification.createTask.newDesign.toLabel')}
              </Typography>
              <Box className={classes.fromToCcValue}>
                <ToField
                  value={toRecipients}
                  onChange={recipients => {
                    setToRecipients(recipients);
                    setCcRecipients(prev => prev.filter(cc => !recipients.find(to => to.object.empId === cc.object.empId)));
                    markAsEdited();
                  }}
                  placementEmployees={placementEmployees}
                  container={optionContainer}
                  excludeEmpIds={ccRecipients.map(r => r.object.empId)}
                  displayClassName={classes.fromToRow}
                  placeholder={t('notification.createTask.newDesign.toPlaceholder')}
                />
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} className={classes.section}>
            <Box className={classes.fromToCcRow}>
              <Typography variant="body2" component="span" className={classes.fromToCcLabel}>
                {t('notification.createTask.ccBtn')}
              </Typography>
              <Box className={classes.fromToCcValue}>
                <ToField
                  value={ccRecipients}
                  onChange={recipients => {
                    setCcRecipients(recipients);
                    markAsEdited();
                  }}
                  placementEmployees={placementEmployees}
                  container={optionContainer}
                  excludeEmpIds={toRecipients.map(r => r.object.empId)}
                  placeholder={t('notification.createTask.newDesign.ccPlaceholder')}
                />
                {hasCrossDuplicates && (
                  <Typography variant="caption" color="error">
                    {t('notification.createTask.newDesign.duplicateRecipientsError')}
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} className={classes.section}>
            <Box className={classes.fromToCcRow}>
              <Typography variant="body2" component="span" className={classes.fromToCcLabel}>
                {t('notification.createTask.newDesign.subjectLabel')}
              </Typography>
              <Box className={classes.fromToCcValue}>
                <Box className={classes.fromToRow}>
                  <TextField
                    fullWidth
                    variant="standard"
                    value={subject}
                    onChange={e => {
                      setSubject(e.target.value);
                      markAsEdited();
                    }}
                    className={classes.subjectInputRow}
                    InputProps={{ disableUnderline: true }}
                  />
                </Box>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} className={classes.section}>
            <Box className={classes.categoryPriorityCheckboxRow}>
              <TextField
                className={classes.categoryPriorityInput}
                variant="filled"
                label={t('notification.createTask.newDesign.categoryLabel')}
                value={categoryDisplay}
                disabled
              />
              <Box className={classes.followUpDatePickerWrapper}>
                <DateRangePicker
                  variant="filled"
                  placeholder={t('notification.createTask.newDesign.followUpDateLabel')}
                  secondaryLabel={t('notification.createTask.newDesign.followUpDateLabel')}
                  useMaxWidth
                  single
                  value={followUpDate}
                  initialDateRange={{
                    startDate: followUpDate ? new Date(followUpDate) : undefined,
                    endDate: followUpDate ? new Date(followUpDate) : undefined,
                    useLabelAsValue: true,
                  }}
                  labelClass={classes.followUpDateLabel}
                  minDate={addDays(new Date(), -1)}
                  maxWidth={160}
                  trailingIconShow
                  isDisabled={false}
                  disablePortal
                  wrapperClass={classes.followUpDateCalendar}
                  compactHeader
                  actionBar={false}
                  clickAway
                  keepClickAwayVal
                  onChange={e => {
                    const next = e?.endDate ? FormatDate(e.endDate.setHours(18)) : '';
                    setFollowUpDate(next);
                    setFollowUpDateError(validateFollowUpDate(next));
                    markAsEdited();
                  }}
                  onBlurText={dateString => {
                    let nextValue = dateString ?? '';
                    if (dateString && dateString.length > 0 && !dateString.includes('_')) {
                      try {
                        const parsedDate = parse(dateString, 'MM/dd/yyyy', new Date());
                        nextValue = FormatDate(addHours(parsedDate, 18));
                      } catch {
                        nextValue = dateString;
                      }
                    }
                    setFollowUpDate(nextValue);
                    setFollowUpDateError(validateFollowUpDate(nextValue));
                    if (nextValue) markAsEdited();
                  }}
                  error={!!followUpDateError}
                  helperText={followUpDateError || ''}
                />
              </Box>
              <TextField
                className={classes.categoryPriorityInput}
                variant="filled"
                label={t('notification.createTask.priority')}
                select
                SelectProps={{ native: true, displayEmpty: true }}
                value={priority}
                onChange={e => {
                  setPriority(e.target.value);
                  markAsEdited();
                }}
              >
                <option value="High">{t('notification.createTask.newDesign.priorityHigh')}</option>
                <option value="Low">{t('notification.createTask.newDesign.priorityLow')}</option>
                <option value="Normal">{t('notification.createTask.newDesign.priorityNormal')}</option>
              </TextField>
              <Box className={classes.checkboxGroup}>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={flagChecked}
                      onChange={e => {
                        setFlagChecked(e.target.checked);
                        markAsEdited();
                      }}
                    />
                  }
                  label={t('notification.createTask.newDesign.flagLabel')}
                  className={classes.checkboxLabel}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={optionApprovalChecked}
                      onChange={e => {
                        setOptionApprovalChecked(e.target.checked);
                        markAsEdited();
                      }}
                    />
                  }
                  label={t('notification.createTask.newDesign.optionApprovalLabel')}
                  className={classes.checkboxLabel}
                />
                <FormControlLabel
                  control={<Checkbox color="primary" checked={followUpChecked} disabled />}
                  label={t('notification.createTask.newDesign.followUpLabel')}
                  className={classes.checkboxLabel}
                />
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} className={`${classes.section} ${classes.messageSection}`}>
            <TextField
              fullWidth
              multiline
              minRows={10}
              maxRows={10}
              variant="filled"
              value={message}
              onChange={e => {
                setMessage(e.target.value);
                markAsEdited();
              }}
              className={classes.messageInput}
            />
          </Grid>

          <Grid item xs={12} className={classes.section}>
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={12} md={5}>
                <Box
                  component="label"
                  htmlFor="reply-task-new-attachment"
                  className={classes.dropzone}
                  onDragOver={e => e.preventDefault()}
                  onDrop={e => {
                    e.preventDefault();
                    handleAddAttachments(Array.from(e.dataTransfer.files));
                  }}
                >
                  <CloudUploadOutlinedIcon className={classes.dropzoneIcon} />
                  <Typography variant="body2" className={classes.dropzoneText} component="span">
                    {t('notification.createTask.newDesign.attachmentDropzoneText')}
                  </Typography>
                  <input
                    type="file"
                    multiple
                    hidden
                    id="reply-task-new-attachment"
                    accept=".jpeg,.jpg,.png,.docx,.doc,.xlsx,.xls,.pdf,.csv,.msg"
                    onChange={e => {
                      const files = e.target.files ? Array.from(e.target.files) : [];
                      handleAddAttachments(files);
                      e.target.value = '';
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={7}>
                {attachments.length > 0 && (
                  <Box className={classes.attachmentList}>
                    {attachments.map((file, index) => (
                      <Box key={`${file.name}-${index}`} className={classes.attachmentItem}>
                        <img
                          src={getAttachmentIcon(file.name.split('.').pop()?.toLowerCase())}
                          alt=""
                          className={classes.attachmentFileIcon}
                        />
                        <Typography variant="body2" noWrap className={classes.attachmentName}>
                          {file.name}
                        </Typography>
                        <IconButton
                          size="small"
                          aria-label={t('notification.createTask.delete')}
                          onClick={() => {
                            setAttachments(prev => prev.filter((_, i) => i !== index));
                            markAsEdited();
                          }}
                          className={classes.attachmentRemoveBtn}
                        >
                          <DeleteOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </GenericDialog>

      <GenericDialog
        open={showDiscardModal}
        maxWidth="sm"
        dialogTitleProps={{ text: t('notification.createTask.newDesign.discardChangesTitle') }}
        dialogActions={[
          {
            text: t('notification.createTask.cancelBtn'),
            onClick: () => setShowDiscardModal(false),
            color: 'tertiary',
          },
          {
            text: t('notification.taskDetails.yesBtn'),
            onClick: () => {
              setShowDiscardModal(false);
              onClose();
            },
            variant: 'contained',
          },
        ]}
      >
        {t('notification.createTask.newDesign.discardChangesBody')}
      </GenericDialog>
    </>
  );
};

